import { useEffect, useId, useRef, useState } from 'react';

import { useAppStoreApps, type AppStoreApp } from '@/lib/app-store-connect';

/**
 * The Apple ID box, as a dropdown of the studio's App Store Connect apps.
 *
 * The number is the thing being asked for — it is what ties a website app to
 * its App Store app — and nobody knows it by heart. So the box is a combobox:
 * open it and the apps App Store Connect lists are there to pick from; type
 * into it and the list narrows by name, bundle ID or number; paste the
 * number and it is simply the number. Picking fills the box with the id and
 * says which app that was underneath, which typing a number never can.
 *
 * The list is fetched when the box opens, not when the dialog does: the
 * request goes to Apple, the server throttles it, and most of the time the
 * person already has the number. When the list cannot be had — no key, a key
 * made for reports only — the box says why in the server's words and still
 * takes a typed number.
 *
 * What is typed is the filter while the list is open; only digits are an
 * Apple ID. "meet" narrows the list and is not sent anywhere; closing the
 * list on "meet" puts the last real number back.
 */
export function AppStorePicker({
  value,
  onChange,
  error,
  autoFocus,
}: {
  value: string;
  /**
   * The number, and the app it came from when it was picked — null when
   * typed, so a bundle ID picked earlier does not stay on a number that no
   * longer belongs to it.
   */
  onChange: (appleId: string, picked: AppStoreApp | null) => void;
  /** A refusal from the server for this field, shown under the box. */
  error?: string | null;
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const [picked, setPicked] = useState<AppStoreApp | null>(null);
  const list = useAppStoreApps(open);
  const listId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  /** The last number this box handed out, so a value change from outside can be told from its own. */
  const emitted = useRef(value);
  /** The last real number, and the app it was picked as, to put back when the list closes on a filter word. */
  const lastNumber = useRef<{ appleId: string; app: AppStoreApp | null }>({
    appleId: value,
    app: null,
  });

  const emit = (appleId: string, app: AppStoreApp | null) => {
    emitted.current = appleId;
    if (appleId !== '') lastNumber.current = { appleId, app };
    onChange(appleId, app);
  };

  // The box follows a value set from outside (a reset, a load), not its own typing.
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    lastNumber.current = { appleId: value, app: null };
    setText(value);
    setPicked(null);
  }, [value]);

  /**
   * Closes the list. A filter word gives way to the last real number, handed
   * back with the app it was picked as — so a word typed and abandoned
   * changes nothing, not even the bundle ID underneath.
   */
  const close = () => {
    setOpen(false);
    if (/^\d*$/.test(text)) return;
    const last = lastNumber.current;
    setText(last.appleId);
    setPicked(last.app);
    if (value !== last.appleId) emit(last.appleId, last.app);
  };

  // No dependency list: `close` reads the current text and value, and the
  // listeners only exist while the list is open, so re-binding per render is
  // cheap and always current.
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (wrapper.current?.contains(event.target as Node)) return;
      close();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        // Only in the box. On an option reached with Tab, Enter is that
        // option's click, and cancelling the keydown would cancel the click.
        if (event.target !== input.current) return;
        // Enter closes the list, as it does any combobox — and does not send
        // the form around it. While the list is out the box may hold a
        // filter word, which is no number at all; an Enter on "meet" that
        // went through to Create would make the app with no Apple ID while
        // the box still says "meet". Closed, the last real number is back,
        // and the next Enter sends that.
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Escape') return;
      // Ours to close, and only ours: the dialog around this listens for the
      // same key, and one Escape must not take a half-filled form with it.
      event.stopPropagation();
      close();
      input.current?.focus();
    };
    document.addEventListener('pointerdown', dismiss, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', dismiss, true);
      document.removeEventListener('keydown', key, true);
    };
  });

  const type = (typed: string) => {
    setText(typed);
    // Digits are the answer, whatever else was pasted around them ("id6478392011");
    // anything else is a word to narrow the list by, and opens it.
    const isNumber = /^\s*(id)?\d*\s*$/i.test(typed);
    const digits = typed.replace(/\D/g, '');
    if (isNumber) {
      if (digits !== value) {
        // A typed number that is one of the listed apps is that app.
        const match = list.data?.apps.find((app) => app.appleId === digits) ?? null;
        setPicked(match);
        emit(digits, match);
      }
      return;
    }
    if (!open) setOpen(true);
    if (value !== '') {
      setPicked(null);
      emit('', null);
    }
  };

  const pick = (app: AppStoreApp) => {
    setPicked(app);
    setText(app.appleId);
    emit(app.appleId, app);
    setOpen(false);
  };

  const needle = text.trim().toLowerCase();
  const shown = (list.data?.apps ?? []).filter(
    (app) =>
      needle === '' ||
      // The app the box already holds stays listed whatever the filter says.
      app.appleId === value ||
      app.name.toLowerCase().includes(needle) ||
      app.bundleId.toLowerCase().includes(needle) ||
      app.appleId.includes(needle.replace(/^id/, '')),
  );
  /** The app behind the number, when the list has been read and holds it. */
  const known = picked ?? list.data?.apps.find((app) => app.appleId === value) ?? null;

  return (
    <div
      ref={wrapper}
      className="flex flex-col gap-[5px]"
      // Tab out closes it too, the way a press elsewhere does — otherwise a
      // filter word left in the box rides along to Save as an empty number.
      // Only when focus really lands somewhere else: Safari and Firefox blur
      // the box with no relatedTarget on a press on a button, and closing
      // then would unmount an option before its click arrives. A press
      // outside is the pointerdown listener's job.
      onBlur={(event) => {
        const to = event.relatedTarget;
        if (open && to instanceof Node && !wrapper.current?.contains(to)) close();
      }}
    >
      <span className="relative block">
        <input
          ref={input}
          value={text}
          role="combobox"
          aria-label="Apple ID"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          placeholder="6478392011 — or pick an app"
          autoFocus={autoFocus}
          onChange={(event) => type(event.target.value)}
          onClick={() => setOpen(true)}
          className="field field-mono field-modal w-full pr-9"
          style={{ borderColor: open ? 'var(--accent)' : undefined }}
        />
        {/*
          The chevron says the box is a list, and turns while it is open — the
          one visible answer to "did that press do anything" that a fetch of
          a few hundred milliseconds otherwise leaves unanswered.
        */}
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? 'Close the list' : 'Pick from App Store Connect'}
          onClick={() => (open ? close() : setOpen(true))}
          className="focus-ring absolute top-1/2 right-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-[6px] border-0 bg-transparent p-0"
          style={{
            color: open ? 'var(--accent)' : 'var(--muted)',
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            transition: 'transform 0.16s ease, color 0.16s ease',
          }}
        >
          <svg
            aria-hidden="true"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2.5 4.5L6 8l3.5-3.5" />
          </svg>
        </button>

        {open ? (
          <div
            id={listId}
            role="listbox"
            aria-label="Apps on App Store Connect"
            className="absolute top-[calc(100%+4px)] right-0 left-0 z-20 flex max-h-[260px] flex-col overflow-y-auto rounded-[10px] border p-1"
            style={{
              background: 'var(--panel)',
              borderColor: 'var(--line2)',
              boxShadow: '0 18px 40px -22px rgba(0, 0, 0, 0.55)',
            }}
          >
            {list.isPending ? (
              <span className="px-2 py-1.5 text-[12px]" style={{ color: 'var(--muted)' }}>
                Asking App Store Connect…
              </span>
            ) : list.isError || list.data?.problem ? (
              <span
                className="px-2 py-1.5 text-[12px] leading-[1.5]"
                role="alert"
                style={{ color: 'var(--muted)' }}
              >
                {list.data?.problem ?? 'Could not reach the API. Type the Apple ID instead.'}
              </span>
            ) : shown.length === 0 ? (
              <span className="px-2 py-1.5 text-[12px]" style={{ color: 'var(--muted)' }}>
                {list.data?.apps.length === 0
                  ? 'App Store Connect lists no apps for this key. Type the Apple ID.'
                  : 'Nothing matches — keep typing the number, or clear the box to see every app.'}
              </span>
            ) : (
              shown.map((app) => {
                const on = app.appleId === value;
                return (
                  <button
                    key={app.appleId}
                    type="button"
                    role="option"
                    aria-selected={on}
                    onClick={() => pick(app)}
                    className="focus-ring flex w-full cursor-pointer flex-col gap-0.5 rounded-[7px] border-0 px-2 py-1.5 text-left hover:bg-[var(--panel2)]"
                    style={{ background: on ? 'var(--panel2)' : 'transparent' }}
                  >
                    <span className="text-[12.5px] font-medium">{app.name}</span>
                    <span className="font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
                      {app.bundleId} · id{app.appleId}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        ) : null}
      </span>

      <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
        {known ? (
          <>
            <span style={{ color: 'var(--text)' }}>{known.name}</span> · {known.bundleId}
          </>
        ) : (
          'The number after “id” in the App Store link. It ties this app to its sales figures and listing.'
        )}
      </span>
      {error ? (
        <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
